import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { AUTH_URL } from '../../util/constants';

type leaveResponsetype = {
  employee_id: string;
  type?: string;
  date: string;
  message: string;
  status: string;
  resolved_by?: {};
};
interface SubmitLeaveApplicationPayload {
  employee_id: string;
  leaveType: string;
  days: number;
  recipientID: string[];
  description: string;
}

type uninformedleaveType = {
  user: {
    _id: string | undefined;
  };
  data: string[];
  category: string | undefined;
  message: string;
};

export const requestApi = createApi({
  reducerPath: 'requestApi',
  baseQuery: fetchBaseQuery({
    baseUrl: AUTH_URL,
  }),
  tagTypes: [
    'requestForLeave',
    'getAllRequests',
    'getAllEmployeeRequests',
    'uninformedLeaveRequest',
    'LeaveData',
  ],
  endpoints: (builder) => ({
    leaveRequest: builder.mutation<any, any>({
      query: (leaveData) => ({
        url: '/leave',
        method: 'POST',
        body: leaveData,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }),
      invalidatesTags: [
        'getAllRequests',
        'getAllEmployeeRequests',
        'uninformedLeaveRequest',
      ],
    }),
    uninformedLeaveRequest: builder.mutation<
      leaveResponsetype,
      uninformedleaveType
    >({
      query: (leaveData) => ({
        url: '/uninformedLeave',
        method: 'POST',
        body: leaveData,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }),
      invalidatesTags: [
        'getAllRequests',
        'getAllEmployeeRequests',
        'requestForLeave',
      ],
    }),
    getAllRequest: builder.query<any, { _id: string | undefined }>({
      query: ({ _id }) => ({
        url: `/getRequests/${_id}`,
        credentials: 'include',
      }),
      providesTags: ['getAllRequests'],
    }),
    getEmployeeRequest: builder.query<any, { _id: string | undefined }>({
      query: ({ _id }) => ({
        url: `/my-requests/${_id}`,
        credentials: 'include',
      }),
      providesTags: ['getAllEmployeeRequests'],
    }),
    resolveRequest: builder.mutation<
      any,
      { id?: string; userId?: string; status?: string; msg?: string }
    >({
      query: ({ id, userId, status, msg }) => ({
        url: '/resolve',
        method: 'POST',
        body: {
          id,
          userId,
          status,
          msg,
        },
        credentials: 'include',
      }),
      invalidatesTags: ['getAllRequests'],
    }),
    getSchedule: builder.query<any, string>({
      query: (id) => ({
        url: `/schedule/${id}`,
        credentials: 'include',
      }),
    }),
    resolveSchedule: builder.mutation<
      any,
      { id?: string; userId?: string; status?: string; msg?: string }
    >({
      query: ({ id, userId, status, msg }) => ({
        url: '/resolve',
        method: 'POST',
        body: {
          id,
          userId,
          status,
          msg,
        },
        credentials: 'include',
      }),
      invalidatesTags: ['getAllRequests'],
    }),
    leaveAdd: builder.mutation<any, Record<string, any>>({
      query: (body) => ({
        url: '/hr/apply',
        method: 'POST',
        body,
        credentials: 'include',
      }),
      invalidatesTags: ['getAllRequests', 'getAllEmployeeRequests'],
    }),
    deleteRequest: builder.mutation<any, { _id: string | null }>({
      query: ({ _id }) => ({
        url: '/delete',
        method: 'POST',
        body: {
          _id,
        },
        credentials: 'include',
      }),
      invalidatesTags: ['getAllRequests', 'getAllEmployeeRequests'],
    }),

    getStatusData: builder.query<any, any>({
      query: ({ startDate, endDate }) => ({
        url: `/status?startDate=${startDate}&endDate=${endDate}`,
        credentials: 'include',
      }),
    }),
    getStatusById: builder.query<any, string>({
      query: (statusId) => ({
        url: `status/${statusId}`,
        credentials: 'include',
      }),
    }),
    submitLeaveApplication: builder.mutation<
      any,
      SubmitLeaveApplicationPayload
    >({
      query: (formData) => ({
        url: '/slack-leave',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: formData,
        credentials: 'include',
      }),
      invalidatesTags: ['LeaveData'],
    }),
    updateAvailability: builder.mutation<
      any,
      { slack_id: string; _id: string }
    >({
      query: ({ slack_id, _id }) => ({
        url: `/slack/notify/${slack_id}`,
        method: 'POST',
        body: {
          id: _id,
        },
        credentials: 'include',
      }),
      invalidatesTags: ['getAllRequests', 'getAllEmployeeRequests'],
    }),
    getEmployeeLeaveBalance: builder.query<any, string>({
      query: (employeeId) => ({
        url: `leave-balance/${employeeId}`,
        credentials: 'include',
      }),
    }),
  }),
});

export const {
  useLeaveRequestMutation,
  useUninformedLeaveRequestMutation,
  useLazyGetAllRequestQuery,
  useLazyGetEmployeeRequestQuery,
  useSubmitLeaveApplicationMutation,
  useResolveRequestMutation,
  useGetScheduleQuery,
  useDeleteRequestMutation,
  useGetStatusByIdQuery,
  useLazyGetStatusDataQuery,
  useLeaveAddMutation,
  useUpdateAvailabilityMutation,
  useLazyGetEmployeeLeaveBalanceQuery,
} = requestApi;
